export type DemandExcelDetailImageRow = {
  label: string;
  value: string;
  changed: boolean;
};

const imageWidth = 720;
const scale = 2;
const padding = 12;
const labelWidth = 132;
const fontSize = 14;
const titleSize = 16;
const lineHeight = 20;

/** 详表图片文件名使用版本号，去掉路径非法字符。 */
export function demandExcelDetailImageFileName(orderVersionKey: string): string {
  const safe = orderVersionKey.replace(/[\\/:*?"<>|\s]+/g, "_").replace(/^_+|_+$/g, "");
  return `${safe || "需求详表"}.png`;
}

/** 按像素宽度把文本拆成行，避免图片里的长字段被裁掉。 */
export function wrapCanvasText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const chars = Array.from(text || "");
  if (chars.length === 0) return [""];
  const lines: string[] = [];
  let current = "";
  for (const char of chars) {
    const next = current + char;
    if (current && context.measureText(next).width > maxWidth) {
      lines.push(current);
      current = char;
    } else {
      current = next;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/** 把需求详表画成 PNG 并触发浏览器下载。 */
export async function downloadDemandExcelDetailImage(input: {
  title: string;
  rows: DemandExcelDetailImageRow[];
  fileName: string;
}): Promise<void> {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("无法创建详表图片");

  const contentWidth = imageWidth - padding * 2;
  const columnWidth = contentWidth / 2;
  const valueWidth = columnWidth - labelWidth;
  context.font = `${fontSize}px sans-serif`;

  const titleLines = wrapCanvasText(
    Object.assign(context, { font: `700 ${titleSize}px sans-serif` }),
    input.title,
    contentWidth,
  );
  context.font = `${fontSize}px sans-serif`;
  const pairs: Array<[DemandExcelDetailImageRow, DemandExcelDetailImageRow | undefined]> = [];
  for (let index = 0; index < input.rows.length; index += 2) {
    pairs.push([input.rows[index], input.rows[index + 1]]);
  }
  const pairLines = pairs.map(([left, right]) => {
    const leftValue = wrapCanvasText(context, left.value || "--", valueWidth - 12);
    const rightValue = right
      ? wrapCanvasText(context, right.value || "--", valueWidth - 12)
      : [""];
    return {
      left,
      right,
      lineCount: Math.max(leftValue.length, rightValue.length, 1),
      leftValue,
      rightValue,
    };
  });
  const titleHeight = titleLines.length * (titleSize + 4) + 8;
  const tableHeight = pairLines.reduce(
    (total, pair) => total + pair.lineCount * lineHeight + 8,
    0,
  );
  const height = padding * 2 + titleHeight + tableHeight;

  canvas.width = imageWidth * scale;
  canvas.height = height * scale;
  context.scale(scale, scale);
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, imageWidth, height);
  context.fillStyle = "#1d2129";
  context.font = `700 ${titleSize}px sans-serif`;
  context.textBaseline = "top";
  titleLines.forEach((line, index) => {
    context.fillText(line, padding, padding + index * (titleSize + 4));
  });

  let top = padding + titleHeight;
  context.font = `${fontSize}px sans-serif`;
  context.strokeStyle = "#1d2129";
  context.lineWidth = 1;
  for (const pair of pairLines) {
    const rowHeight = pair.lineCount * lineHeight + 8;
    drawCell(context, padding, top, labelWidth, rowHeight, pair.left.label, false, true);
    drawCell(
      context,
      padding + labelWidth,
      top,
      valueWidth,
      rowHeight,
      pair.leftValue,
      pair.left.changed,
      false,
    );
    if (pair.right) {
      drawCell(
        context,
        padding + columnWidth,
        top,
        labelWidth,
        rowHeight,
        pair.right.label,
        false,
        true,
      );
      drawCell(
        context,
        padding + columnWidth + labelWidth,
        top,
        valueWidth,
        rowHeight,
        pair.rightValue,
        pair.right.changed,
        false,
      );
    } else {
      drawCell(context, padding + columnWidth, top, columnWidth, rowHeight, "", false, false);
    }
    top += rowHeight;
  }

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((result) => {
      if (result) resolve(result);
      else reject(new Error("详表图片为空"));
    }, "image/png");
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = input.fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** 画一个带边框的标签或值格子，变更值使用红底白字。 */
function drawCell(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  text: string | string[],
  changed: boolean,
  label: boolean,
) {
  context.fillStyle = changed ? "#ff0000" : label ? "#fafafa" : "#ffffff";
  context.fillRect(x, y, width, height);
  context.strokeRect(x, y, width, height);
  context.fillStyle = changed ? "#ffffff" : "#1d2129";
  context.font = label ? `700 ${fontSize}px sans-serif` : `${fontSize}px sans-serif`;
  const lines = Array.isArray(text) ? text : wrapCanvasText(context, text, width - 12);
  lines.forEach((line, index) => {
    context.fillText(line, x + 6, y + 4 + index * lineHeight);
  });
}
